import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@artosera/ui';
import { CLE_SESSION, useSession } from '../../auth/useSession.js';
import { usePeut } from '../../auth/usePeut.js';
import { lireVueDAigle } from '../../lib/api.js';
import { PERIODES_VUE_D_AIGLE } from '@artosera/shared';
import type { NomVueDAigle, PeriodeVueDAigle, VueDAigle as Vue } from '../../types.js';
import { ATraiter } from './ATraiter.js';
import commun from './commun.module.css';
import { CoutsMarge } from './CoutsMarge.js';
import { useFormats } from './formats.js';
import { Indicateurs } from './Indicateurs.js';
import { Jalons } from './Jalons.js';
import { Modules } from './Modules.js';
import { Parc } from './Parc.js';
import { Partage } from './Partage.js';
import { Revenus } from './Revenus.js';
import { Sante } from './Sante.js';
import { SommesDues } from './SommesDues.js';
import { Usage } from './Usage.js';
import styles from './VueDAigle.module.css';

/** Les sections de la page, dans l'ordre, avec ce que le sommaire en dit. */
type Section =
  | 'aTraiter'
  | 'revenus'
  | 'sommesDues'
  | 'parc'
  | 'modules'
  | 'usage'
  | 'sante'
  | 'couts'
  | 'jalons'
  | 'partage';

const ANCRES: Record<Section, string> = {
  aTraiter: 'a-traiter',
  revenus: 'revenus',
  sommesDues: 'sommes-dues',
  parc: 'parc',
  modules: 'modules',
  usage: 'usage',
  sante: 'sante',
  couts: 'couts',
  jalons: 'jalons',
  partage: 'partage',
};

/** Les sections que cette vue porte : l'API a déjà filtré, la page n'a rien à masquer. */
function sectionsDe(vue: Vue): Section[] {
  return [
    ...(vue.aTraiter ? (['aTraiter'] as const) : []),
    'revenus',
    'sommesDues',
    'parc',
    'modules',
    'usage',
    'sante',
    'couts',
    'jalons',
    ...(vue.partage.length > 0 ? (['partage'] as const) : []),
  ];
}

/**
 * Accueil de la console (ARTO-58) : la seule page que tout rôle peut lire, et
 * la seule de l'associé. L'API filtre selon le rôle (`vue` de la réponse) : la
 * page rend ce qu'elle reçoit. « Voir comme un associé » redemande la vue avec
 * `vue=associe` : l'aperçu est celui du serveur, pas un masquage côté front.
 */
export function VueDAigle(): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const { moi } = useSession();
  const peut = usePeut();
  const queryClient = useQueryClient();
  // La période vit dans l'adresse (`?periode=30j`) : on peut la partager, et
  // actualiser la page ne la perd pas. La valeur par défaut n'y figure pas.
  const [parametres, setParametres] = useSearchParams();
  const demandee = parametres.get('periode');
  const periode: PeriodeVueDAigle = (PERIODES_VUE_D_AIGLE as readonly string[]).includes(
    demandee ?? '',
  )
    ? (demandee as PeriodeVueDAigle)
    : '12m';
  const setPeriode = (valeur: PeriodeVueDAigle): void => {
    setParametres(
      (precedents) => {
        const suivants = new URLSearchParams(precedents);
        if (valeur === '12m') suivants.delete('periode');
        else suivants.set('periode', valeur);
        return suivants;
      },
      { replace: true },
    );
  };
  const [apercu, setApercu] = useState(false);

  // Qui peut lire la console peut aussi regarder en associé ; un associé, non.
  const operationnel = peut('console:read');
  const vueDemandee: NomVueDAigle | undefined = apercu ? 'associe' : undefined;
  const vueAttendue: NomVueDAigle = apercu || !operationnel ? 'associe' : 'admin';

  // La clé porte le compte, son rôle et la vue demandée : la réponse en cache
  // d'un autre compte, d'un rôle d'avant (rétrogradation) ou d'une autre vue
  // n'est jamais servie.
  const compteId = moi?.compte.id ?? null;
  const role = moi?.compte.rolePlateforme ?? null;
  const requete = useQuery({
    queryKey: ['console', 'vue-d-aigle', compteId, role, periode, vueDemandee ?? 'defaut'],
    queryFn: () => lireVueDAigle(periode, vueDemandee),
    enabled: moi !== undefined,
    // Changer de période garde la page à l'écran — pour le MÊME compte, au
    // MÊME rôle, dans la même vue. Changer de vue, jamais : on n'affiche pas
    // un instant la vue complète sous le bandeau « Aperçu associé ». Changer
    // de compte ou de rôle, jamais non plus.
    placeholderData: (precedente, requetePrecedente) =>
      requetePrecedente?.queryKey[2] === compteId &&
      requetePrecedente.queryKey[3] === role &&
      precedente?.vue === vueAttendue
        ? precedente
        : undefined,
  });

  // Une réponse qui n'est pas la vue attendue ne s'affiche JAMAIS : la page
  // reste en chargement. Fraîche et pourtant différente, elle dit que le rôle
  // a changé côté serveur (rétrogradation) : la session est relue.
  const recue = requete.data;
  const vue = recue?.vue === vueAttendue ? recue : undefined;
  const decalee = recue !== undefined && !requete.isPlaceholderData && recue.vue !== vueAttendue;
  useEffect(() => {
    if (decalee) void queryClient.invalidateQueries({ queryKey: CLE_SESSION });
  }, [decalee, queryClient]);
  const sections = vue ? sectionsDe(vue) : [];
  const numero = (section: Section): string =>
    String(sections.indexOf(section) + 1).padStart(2, '0');

  return (
    <div aria-busy={requete.isFetching || undefined}>
      <div className={styles.entete}>
        <div>
          <h1>{t('vueDAigle.titre')}</h1>
          <p>
            {vue ? t('vueDAigle.texte', { date: f.jourMoisAnnee(vue.genereeLe) }) : null}
            {vue?.vue === 'admin' ? ` ${t('vueDAigle.texteAdmin')}` : null}
          </p>
        </div>
        <div className={styles.actions}>
          {vue?.vue === 'associe' && !apercu ? (
            <span className={`${commun.pastille} ${commun.pastilleGris}`}>
              {t('vueDAigle.lectureSeule')}
            </span>
          ) : null}
          {operationnel && !apercu ? (
            <Button variant="secondary" onClick={() => setApercu(true)}>
              {t('vueDAigle.voirCommeAssocie')}
            </Button>
          ) : null}
        </div>
      </div>

      {apercu ? (
        <div className={styles.bandeau} role="status">
          <span>
            <strong>{t('vueDAigle.apercu.titre')}</strong>. {t('vueDAigle.apercu.texte')}
          </span>
          <Button variant="secondary" size="sm" onClick={() => setApercu(false)}>
            {t('vueDAigle.apercu.revenir')}
          </Button>
        </div>
      ) : null}

      <div className={styles.periodeLigne}>
        <div className={styles.periode} role="group" aria-label={t('vueDAigle.periode.aria')}>
          {PERIODES_VUE_D_AIGLE.map((valeur) => (
            <button
              key={valeur}
              type="button"
              aria-pressed={periode === valeur}
              onClick={() => setPeriode(valeur)}
            >
              {t(`vueDAigle.periode.${valeur}`)}
            </button>
          ))}
        </div>
        <span className={commun.note}>{t(`vueDAigle.periode.note.${periode}`)}</span>
      </div>

      {requete.isError ? (
        <p className={styles.etat} role="alert">
          {t('vueDAigle.erreur')}
        </p>
      ) : !vue ? (
        <p className={styles.etat} role="status">
          {t('vueDAigle.chargement')}
        </p>
      ) : (
        <>
          {vue.aTraiter ? <ATraiter lignes={vue.aTraiter} numero={numero('aTraiter')} /> : null}
          <Indicateurs vue={vue} />
          <nav className={styles.sommaire} aria-label={t('vueDAigle.sommaire.aria')}>
            {sections.map((section) => (
              <a key={section} href={`#${ANCRES[section]}`}>
                <span className={styles.n} aria-hidden="true">
                  {numero(section)}
                </span>
                {t(`vueDAigle.sommaire.${section}`)}
              </a>
            ))}
          </nav>
          <Revenus vue={vue} numero={numero('revenus')} />
          <SommesDues vue={vue} numero={numero('sommesDues')} />
          <Parc vue={vue} numero={numero('parc')} />
          <Modules vue={vue} numero={numero('modules')} />
          <Usage vue={vue} numero={numero('usage')} />
          <Sante vue={vue} numero={numero('sante')} />
          <CoutsMarge vue={vue} numero={numero('couts')} />
          <Jalons vue={vue} numero={numero('jalons')} />
          {vue.partage.length > 0 ? <Partage vue={vue} numero={numero('partage')} /> : null}
        </>
      )}
    </div>
  );
}
